import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Megaphone, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Announcements() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const canPost = ["admin", "practitioner"].includes(user?.role);

  const { data: items = [] } = useQuery({ queryKey: ["announcements"], queryFn: async () => (await api.get("/announcements")).data });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/announcements", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["announcements"] }); setOpen(false); setForm({ title: "", body: "" }); toast.success("Announcement posted"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Announcements</h1>
          <p className="mt-1 text-sm text-muted-foreground">Practice-wide notices</p>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-announcement-btn" className="gap-2 rounded-full"><Plus className="h-4 w-4" /> New notice</Button>
            </DialogTrigger>
            <DialogContent className="bg-popover">
              <DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Message</Label><Textarea data-testid="announcement-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="min-h-[100px]" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="announcement-submit" className="rounded-full" disabled={!form.title || createMut.isPending} onClick={() => createMut.mutate(form)}>Post notice</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="rounded-2xl border-border/60 p-12 text-center text-muted-foreground"><Megaphone className="mx-auto mb-2 h-8 w-8" /> No announcements yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id} data-testid={`announcement-${a.id}`} className="lift rounded-2xl border-border/60 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Megaphone className="h-4 w-4" /></div>
                <div className="flex-1">
                  <div className="font-semibold">{a.title}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{a.body}</p>
                  <div className="mt-2 text-xs text-muted-foreground">{a.created_by} · {new Date(a.created_at).toLocaleDateString("en-ZA")}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
